// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline component for visualizing asset logistics events.
 * This module provides a comprehensive timeline view that shows
 * the movement and logistics history of an asset, including
 * shipments, locations, and planned movements. It supports
 * zooming, scrolling, and interactive features.
 *
 * Key features:
 * - Movement history
 * - Shipment tracking
 * - Interactive timeline
 * - Zoom controls
 * - Scrollable view
 * - Event tooltips
 */
import { getEventGroupConfigurations } from '@/asset/api/get-event-group-configurations';
import { getEventGroups } from '@/asset/api/get-event-groups';
import { Asset, EventGroup } from '@/asset/api/types';
import { AssetShipmentTimelineSegment } from '@/asset/components/asset-shipment-timeline-segment';
import { AssetShipmentTimelineTooltip } from '@/asset/components/asset-shipment-timeline-tooltip';
import { EventGroupDialog } from '@/asset/components/event-group-dialog';
import { useEventGroups } from '@/asset/hooks/use-event-groups';
import { ScrollIndicator } from '@/components/scroll-indicator';
import { TimelineAxis } from '@/components/timeline-axis';
import { TimelineColumns } from '@/components/timeline-columns';
import { TimelineCurrentDateIndicator } from '@/components/timeline-current-date-indicator';
import { TimelineDensity } from '@/constants/timeline-density';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { useScrollIndicator } from '@/hooks/use-scroll-indicator';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { localPoint } from '@visx/event';
import { scaleTime } from '@visx/scale';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { isNil, maxBy, minBy } from 'lodash-es';
import { Interval } from 'luxon';
import { MouseEvent, useEffect, useMemo, useRef } from 'react';

/**
 * Minimum number of rows to display in the timeline.
 */
const MINIMUM_ROW_COUNT = 3;

/**
 * Maximum number of rows to display in the timeline.
 */
const MAXIMUM_ROW_COUNT = 3;

/**
 * Height of the timeline axis in pixels.
 */
const AXIS_HEIGHT = 40;

/**
 * Props for configuring the AssetLogisticsTimeline component.
 *
 * @interface Props
 */
interface Props {
  asset: Asset;

  /**
   * Current zoom level of the timeline.
   */
  zoom: TimelineDensity;

  /**
   * Time interval to display in the timeline.
   */
  interval: Interval;

  /**
   * Height of each timeline bar in pixels.
   */
  barHeight: number;

  /**
   * Vertical margin between timeline bars in pixels.
   */
  barMargin: number;

  /**
   * Whether timeline interactions are disabled.
   * When true, prevents editing and interaction.
   *
   * @default false
   */
  isDisabled?: boolean;
}

/**
 * A timeline component for visualizing asset logistics events.
 * Displays a comprehensive timeline view showing the movement
 * and logistics history of an asset, including shipments,
 * locations, and planned movements. Supports zooming,
 * scrolling, and interactive features.
 *
 * Features:
 * - Movement history
 * - Shipment tracking
 * - Interactive timeline
 * - Zoom controls
 * - Scrollable view
 * - Event tooltips
 * - Current date indicator
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   return (
 *     <AssetLogisticsTimeline
 *       asset={asset}
 *       zoom="week"
 *       interval={interval}
 *       barHeight={30}
 *       barMargin={8}
 *     />
 *   );
 * }
 * ```
 */
export function AssetLogisticsTimeline(props: Props) {
  const theme = useTheme();
  const svgRef = useRef<SVGElement | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const locationDialog = useDisclosure();
  const routeApi = getRouteApi('/_app/assets/$assetKey');
  const routeSearch = routeApi.useSearch();
  const routeNavigate = routeApi.useNavigate();
  const editEventGroupKey = routeSearch.editEventGroup;
  const editDialog = useDisclosure(editEventGroupKey !== undefined);

  // Sync dialog state when the search param changes (e.g., navigating from movements table)
  const openEditDialog = editDialog.open;
  useEffect(() => {
    if (editEventGroupKey !== undefined) {
      openEditDialog();
    }
  }, [editEventGroupKey, openEditDialog]);

  const handleCloseEditDialog = () => {
    editDialog.close();
    // Clear the deep link from the URL without pushing to history
    void routeNavigate({
      search: { editEventGroup: undefined },
      replace: true,
    });
  };
  const resizeObserver = useResizeObserver(boxRef);
  const scrollIndicator = useScrollIndicator();

  const {
    tooltipTop,
    tooltipLeft,
    tooltipOpen,
    tooltipData,
    showTooltip,
    hideTooltip,
  } = useTooltip<EventGroup>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const configurationsQuery = useSuspenseQuery({
    queryKey: ['event-group-configurations'],
    queryFn: getEventGroupConfigurations,
  });

  const eventGroupQuery = useEventGroups(
    props.asset.key,
    { start: props.interval.start, end: props.interval.end },
    props.interval.length() > 0
  );

  // When deep-linked, fetch all event groups for the asset (no date filter) so
  // the target AEG is always available even if it falls outside the visible timeline interval.
  const deepLinkQuery = useQuery({
    queryKey: [
      'asset',
      props.asset.key,
      'event-group',
      'deep-link',
      editEventGroupKey,
    ],
    queryFn: () => getEventGroups(props.asset.key),
    enabled: editEventGroupKey !== undefined,
  });

  // Find the event group to edit — first try the filtered timeline data,
  // then fall back to the unfiltered deep-link query
  const editEventGroup = useMemo(() => {
    if (!editEventGroupKey) {
      return undefined;
    }

    const fromTimeline = eventGroupQuery.data?.eventGroups.find(
      (eg) => eg.key === editEventGroupKey
    );
    if (fromTimeline) {
      return fromTimeline;
    }

    return deepLinkQuery.data?.eventGroups.find(
      (eg) => eg.key === editEventGroupKey
    );
  }, [editEventGroupKey, eventGroupQuery.data, deepLinkQuery.data]);

  // Create time scale for timeline
  const dateScale = scaleTime({
    range: [40, resizeObserver.width - 40],
    domain: [props.interval.start.toJSDate(), props.interval.end.toJSDate()],
  });

  // Unfortunately the API does not provide a concrete "shipment" interface on
  // top of the generic "event group" interface, so an assumption is made that
  // there is only one event group configuration.
  const configuration = configurationsQuery.data.configurations[0];

  // Filter and sort event groups within interval
  const eventGroups =
    eventGroupQuery.data?.eventGroups
      .slice()
      .filter(({ events }) => {
        const firstEvent = minBy(
          events,
          ({ occurredAt, expectedAt }) => occurredAt ?? expectedAt
        );

        const lastEvent = maxBy(
          events,
          ({ occurredAt, expectedAt }) => occurredAt ?? expectedAt
        );

        const startDateTime = firstEvent?.occurredAt ?? firstEvent?.expectedAt;
        const endDateTime = lastEvent?.occurredAt ?? lastEvent?.expectedAt;

        const hasValidStart =
          !isNil(startDateTime) && startDateTime <= props.interval.end;

        const hasValidEnd =
          !isNil(endDateTime) && endDateTime >= props.interval.start;

        return hasValidStart && hasValidEnd;
      })
      .sort((eventGroup, other) => {
        const eventGroupDateTime =
          eventGroup.minimumDate ?? eventGroup.maximumDate;

        const otherDateTime = other.minimumDate ?? other.maximumDate;

        if (eventGroupDateTime === null && otherDateTime === null) {
          return 0;
        }

        if (eventGroupDateTime === null) {
          return 1;
        }

        if (otherDateTime === null) {
          return -1;
        }

        return eventGroupDateTime.toMillis() - otherDateTime.toMillis();
      }) ?? [];

  // Calculate timeline dimensions
  const rowHeight = props.barHeight + props.barMargin * 2;
  const mininumHeight = rowHeight * MINIMUM_ROW_COUNT;
  const maximumHeight = rowHeight * MAXIMUM_ROW_COUNT;
  const height = Math.max(rowHeight * eventGroups.length, mininumHeight);

  /**
   * Calculates vertical position for a timeline row.
   *
   * @param index - Row index (0-based)
   * @returns Y coordinate in pixels
   */
  const getY = (index: number) => {
    return index * rowHeight + props.barMargin;
  };

  /**
   * Handles click on scroll indicator.
   * Scrolls timeline to bottom smoothly.
   */
  const handleClickScroll = () => {
    if (scrollIndicator.element === null) {
      return;
    }

    scrollIndicator.element.scrollTo({
      top: scrollIndicator.element.scrollHeight,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  /**
   * Handles container ref assignment.
   * Updates both local and tooltip container refs.
   *
   * @param element - SVG element reference
   */
  const handleContainerRef = (element: SVGElement | null) => {
    svgRef.current = element;
    containerRef(element);
  };

  /**
   * Creates mouse move handler for event group.
   * Shows tooltip with event group data.
   *
   * @param eventGroup - Event group to show in tooltip
   * @returns Mouse move event handler
   */
  const createMouseMoveHandler =
    (eventGroup: EventGroup) => (event: MouseEvent<SVGElement>) => {
      if (svgRef.current === null) {
        return;
      }

      const point = localPoint(svgRef.current, event);

      if (point === null) {
        return;
      }

      showTooltip({
        tooltipTop: point.y,
        tooltipLeft: point.x,
        tooltipData: eventGroup,
      });
    };

  /**
   * Handles mouse leave events.
   * Hides the tooltip.
   */
  const handleMouseLeave = () => {
    hideTooltip();
  };

  return (
    <Box ref={boxRef} position="relative">
      <EventGroupDialog
        asset={props.asset}
        isOpen={locationDialog.isOpen}
        onClose={locationDialog.close}
        configuration={configuration}
      />
      <EventGroupDialog
        asset={props.asset}
        isOpen={editDialog.isOpen && editEventGroup !== undefined}
        onClose={handleCloseEditDialog}
        eventGroup={editEventGroup}
        configuration={editEventGroup?.configuration ?? configuration}
      />
      <Stack
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack>
          <Typography variant="subtitle2">Asset movement</Typography>
          <Typography variant="body2" color="text.secondary">
            Logistics history for this asset
          </Typography>
        </Stack>
        <Button
          color="secondary"
          variant="contained"
          onClick={locationDialog.open}
          disabled={props.isDisabled}
        >
          Move / Plan
        </Button>
      </Stack>
      <svg width="100%" height={AXIS_HEIGHT} style={{ display: 'block' }}>
        <TimelineAxis
          scale={dateScale}
          width={resizeObserver.width}
          density={props.zoom}
          interval={props.interval}
          axisHeight={AXIS_HEIGHT}
          chartHeight={AXIS_HEIGHT}
        />
      </svg>
      <Box
        ref={scrollIndicator.ref}
        minHeight={mininumHeight}
        maxHeight={maximumHeight}
        overflow="auto"
      >
        <ScrollIndicator
          label="More movements"
          isVisible={scrollIndicator.isVisible}
          onClick={handleClickScroll}
        />
        <svg
          ref={handleContainerRef}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          <TimelineProvider>
            {tooltipOpen && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  padding: 0,
                  zIndex: theme.zIndex.tooltip,
                  color: theme.palette.text.primary,
                }}
              >
                {tooltipData && (
                  <AssetShipmentTimelineTooltip shipment={tooltipData} />
                )}
              </TooltipInPortal>
            )}
            <TimelineColumns
              y={0}
              scale={dateScale}
              height={height}
              density={props.zoom}
              interval={props.interval}
            />
            <TimelineCurrentDateIndicator
              y={0}
              height={height}
              scale={dateScale}
            />
            {eventGroups.map((segment, index) => {
              const nextEventGroup =
                index < eventGroups.length ? eventGroups[index + 1] : null;

              const previousEventGroup =
                index > 0 ? eventGroups[index - 1] : null;

              const rawMaximum =
                nextEventGroup?.minimumDate ?? props.interval.end;
              const maximumDateTime =
                rawMaximum > props.interval.end
                  ? props.interval.end
                  : rawMaximum;

              const rawMinimum =
                previousEventGroup?.maximumDate ?? props.interval.start;
              const minimumDateTime =
                rawMinimum < props.interval.start
                  ? props.interval.start
                  : rawMinimum;

              return (
                <AssetShipmentTimelineSegment
                  isEditable
                  key={segment.id}
                  y={getY(index)}
                  scale={dateScale}
                  asset={props.asset}
                  height={props.barHeight}
                  shipment={segment}
                  minimumDateTime={minimumDateTime}
                  maximumDateTime={maximumDateTime}
                  onMouseMove={createMouseMoveHandler(segment)}
                  onMouseLeave={handleMouseLeave}
                />
              );
            })}
          </TimelineProvider>
        </svg>
      </Box>
    </Box>
  );
}
